import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Clock, DollarSign, Filter, Loader2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const fmtMoney = (n) => n == null ? null : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const monthNames = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];
const daysOfWeek = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

export default function Calendar() {
  const [cursor, setCursor] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [scope, setScope] = useState("mine"); // mine | all-saved | all
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const monthLabel = `${monthNames[cursor.getMonth()]} ${cursor.getFullYear()}`;

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        if (scope === "mine") {
          const res = await api.get("/applications");
          const apps = res.data.applications || [];
          const withGrants = await Promise.all(apps.map(async (a) => {
            const g = await api.get(`/grants/${a.grant_id}`).then((r) => r.data).catch(() => null);
            return g && g.deadline ? { id: a.id, title: a.grant_title, agency: a.grant_agency,
              deadline: g.deadline, amount: g.funding_max, source: "application", link: `/applications` } : null;
          }));
          setItems(withGrants.filter(Boolean));
        } else if (scope === "saved") {
          const res = await api.get("/grants/saved/list");
          setItems((res.data.grants || []).filter((g) => g.deadline).map((g) => ({
            id: g.id, title: g.title, agency: g.agency, deadline: g.deadline,
            amount: g.funding_max, source: "saved", link: `/grants/${g.id}`,
          })));
        } else {
          // All - upcoming from db
          const res = await api.get("/grants?limit=200");
          setItems((res.data.grants || []).filter((g) => g.deadline).map((g) => ({
            id: g.id, title: g.title, agency: g.agency, deadline: g.deadline,
            amount: g.funding_max, source: "grant", link: `/grants/${g.id}`,
          })));
        }
      } finally {
        setLoading(false);
      }
    }
    load();
    // Only `scope` is reactive; api and setters are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  const monthItems = useMemo(() => {
    const y = cursor.getFullYear(), m = cursor.getMonth();
    return items
      .map((it) => ({ ...it, _d: new Date(it.deadline) }))
      .filter((it) => it._d.getFullYear() === y && it._d.getMonth() === m)
      .sort((a, b) => a._d - b._d);
  }, [items, cursor]);

  const byDay = useMemo(() => {
    const map = {};
    for (const it of monthItems) {
      const day = it._d.getDate();
      (map[day] ||= []).push(it);
    }
    return map;
  }, [monthItems]);

  const firstOfMonth = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
  const leadingBlanks = firstOfMonth.getDay();
  const today = new Date();
  const isToday = (day) =>
    today.getFullYear() === cursor.getFullYear() &&
    today.getMonth() === cursor.getMonth() &&
    today.getDate() === day;

  const next = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1));
  const prev = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1));
  const goToday = () => {
    const t = new Date();
    setCursor(new Date(t.getFullYear(), t.getMonth(), 1));
  };

  const cells = [];
  for (let i = 0; i < leadingBlanks; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div className="space-y-8" data-testid="calendar-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow mb-2">Deadlines</div>
          <h1 className="font-heading font-light text-4xl md:text-5xl tracking-tighter">Calendar</h1>
          <p className="text-[#5E5E5E] mt-2">Every close date in one place. Reminders arrive at 14, 7, and 2 days out.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger className="w-[200px] bg-white border-[#E6E4DF]" data-testid="calendar-scope-select">
              <div className="flex items-center gap-2"><Filter className="w-4 h-4"/><SelectValue/></div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="mine" data-testid="scope-mine">My applications</SelectItem>
              <SelectItem value="saved" data-testid="scope-saved">Saved grants</SelectItem>
              <SelectItem value="all" data-testid="scope-all">All grants</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </header>

      <div className="bg-white border border-[#E6E4DF] rounded-lg overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E6E4DF]">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={prev} data-testid="calendar-prev"><ChevronLeft className="w-4 h-4"/></Button>
            <div className="font-heading text-xl font-medium min-w-[200px] text-center" data-testid="calendar-month-label">{monthLabel}</div>
            <Button variant="ghost" size="icon" onClick={next} data-testid="calendar-next"><ChevronRight className="w-4 h-4"/></Button>
          </div>
          <Button variant="outline" onClick={goToday} data-testid="calendar-today" className="border-[#E6E4DF]">
            Today
          </Button>
        </div>

        {loading ? (
          <div className="py-24 text-center text-[#5E5E5E]"><Loader2 className="w-6 h-6 animate-spin inline"/></div>
        ) : (
          <>
            <div className="grid grid-cols-7 border-b border-[#E6E4DF]">
              {daysOfWeek.map((d) => (
                <div key={d} className="py-3 text-center text-xs uppercase tracking-widest text-[#7A8C77] font-bold border-r border-[#E6E4DF] last:border-r-0">
                  {d}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7">
              {cells.map((day, idx) => (
                <div key={day ? `d-${day}` : `blank-${idx}`} className={`min-h-[120px] p-2 border-r border-b border-[#E6E4DF] ${
                  idx % 7 === 6 ? "border-r-0" : ""
                } ${day && isToday(day) ? "bg-[#E8ECEB]" : "bg-white"}`}
                  data-testid={day ? `cal-day-${day}` : undefined}>
                  {day && (
                    <>
                      <div className={`text-xs font-medium mb-1 ${isToday(day) ? "text-[#1C3F35]" : "text-[#5E5E5E]"}`}>
                        {day}{isToday(day) && <span className="ml-1 text-[10px] uppercase tracking-widest text-[#C1654B] font-bold">Today</span>}
                      </div>
                      <div className="space-y-1">
                        {(byDay[day] || []).slice(0, 3).map((it) => (
                          <Link key={it.id} to={it.link} data-testid={`cal-event-${it.id}`}
                            className="block px-2 py-1 rounded text-[11px] leading-tight bg-[#1C3F35] text-white hover:bg-[#1A362D] truncate">
                            {it.title}
                          </Link>
                        ))}
                        {(byDay[day] || []).length > 3 && (
                          <div className="text-[10px] text-[#5E5E5E]">+{(byDay[day] || []).length - 3} more</div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Upcoming list */}
      <section className="bg-white border border-[#E6E4DF] rounded-lg p-6" data-testid="upcoming-deadlines">
        <h2 className="font-heading text-xl font-medium mb-4">This month at a glance</h2>
        {monthItems.length === 0 ? (
          <div className="text-[#5E5E5E] text-sm">Nothing this month.</div>
        ) : (
          <ul className="divide-y divide-[#E6E4DF]">
            {monthItems.slice(0, 20).map((it) => (
              <li key={it.id} className="py-3 flex items-start justify-between gap-4" data-testid={`upcoming-${it.id}`}>
                <div className="min-w-0">
                  <Link to={it.link} className="font-medium text-[#1A1A1A] hover:text-[#1C3F35] truncate block">{it.title}</Link>
                  <div className="text-xs text-[#5E5E5E] mt-0.5">{it.agency}</div>
                </div>
                <div className="text-right shrink-0 text-xs text-[#5E5E5E]">
                  <div className="flex items-center gap-1 justify-end"><Clock className="w-3 h-3"/>{it._d.toLocaleDateString(undefined, { month: "short", day: "numeric" })}</div>
                  {it.amount && <div className="flex items-center gap-1 justify-end mt-1"><DollarSign className="w-3 h-3"/>{fmtMoney(it.amount)}</div>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
